import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Shared hooks
import { useAuth } from '../../hooks/auth/useAuth';

/**
 * Role-Based Automatic Redirect Component
 *
 * Automatically redirects users to the appropriate portal/page based on their roles and permissions.
 * Useful for post-login navigation and role-based routing.
 *
 * @param {Object} props
 * @param {boolean} [props.immediate] - Whether to redirect immediately or wait for manual trigger
 * @param {Object} [props.customRoutes] - Custom route mappings based on role levels
 * @param {string} [props.fallbackRoute] - Default route if no specific role route is found
 * @param {boolean} [props.considerOrganizationType] - Whether to factor in organization type for routing
 */
const RoleBasedRedirect = ({
  immediate = true,
  customRoutes = {},
  fallbackRoute = '/dashboard',
  considerOrganizationType = true,
}) => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isAdmin,
    isSupervisor,
    maxRoleLevel,
    currentOrgType,
    currentOrganization,
    roles,
  } = useAuth();

  // Default role-based routing
  const getDefaultRoute = () => {
    // Custom routes take precedence
    if (customRoutes[maxRoleLevel]) {
      return customRoutes[maxRoleLevel];
    }

    // Consider organization type in routing
    if (considerOrganizationType && currentOrgType) {
      // Super Admin - always goes to super admin portal
      if (isSuperAdmin) {
        return '/super-admin';
      }

      // FleetCam organization routing
      if (currentOrgType === 'fleetcam') {
        if (isAdmin) {
          return '/admin';
        }
        if (isSupervisor) {
          return '/bureau';
        }
        return '/bureau'; // Default for FleetCam users
      }

      // Client organization routing
      if (currentOrgType === 'client') {
        if (isAdmin) {
          return '/client/admin';
        }
        if (isSupervisor) {
          return '/client/management';
        }
        return '/client/dashboard'; // Default for client users
      }
    }

    // Fallback role-based routing (without organization context)
    if (isSuperAdmin) {
      return '/super-admin';
    }
    if (isAdmin) {
      return '/admin';
    }
    if (isSupervisor) {
      return '/management';
    }

    return fallbackRoute;
  };

  // Special role-based routing
  const getSpecialRoute = () => {
    if (!roles || roles.length === 0) return null;

    // Check for specific roles that need special routing
    const roleRoutes = {
      'Sales Manager': '/sales',
      'Bureau Agent': '/bureau',
      Executive: '/executive',
      Developer: '/dev',
      'Account Manager': '/accounts',
    };

    for (const role of roles) {
      if (roleRoutes[role.name]) {
        return roleRoutes[role.name];
      }
    }

    return null;
  };

  const performRedirect = () => {
    if (!isAuthenticated || isLoading) return;

    // Check for special role routing first
    const specialRoute = getSpecialRoute();
    if (specialRoute) {
      navigate(specialRoute, { replace: true });
      return;
    }

    // Use default role-based routing
    const defaultRoute = getDefaultRoute();
    navigate(defaultRoute, { replace: true });
  };

  useEffect(() => {
    if (immediate) {
      performRedirect();
    }
  }, [
    immediate,
    isAuthenticated,
    isLoading,
    maxRoleLevel,
    currentOrgType,
    currentOrganization,
    roles,
  ]);

  // This component doesn't render anything - it just performs redirects
  return null;
};

RoleBasedRedirect.propTypes = {
  immediate: PropTypes.bool,
  customRoutes: PropTypes.object,
  fallbackRoute: PropTypes.string,
  considerOrganizationType: PropTypes.bool,
};

/**
 * Hook version of RoleBasedRedirect for programmatic use
 */
export const useRoleBasedRedirect = (options = {}) => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isAdmin,
    isSupervisor,
    maxRoleLevel,
    currentOrgType,
    roles,
  } = useAuth();

  const {
    customRoutes = {},
    fallbackRoute = '/dashboard',
    considerOrganizationType = true,
  } = options;

  const getRedirectPath = () => {
    if (!isAuthenticated || isLoading) return null;

    // Custom routes take precedence
    if (customRoutes[maxRoleLevel]) {
      return customRoutes[maxRoleLevel];
    }

    // Special role routing
    if (roles && roles.length > 0) {
      const roleRoutes = {
        'Sales Manager': '/sales',
        'Bureau Agent': '/bureau',
        Executive: '/executive',
        Developer: '/dev',
        'Account Manager': '/accounts',
      };

      for (const role of roles) {
        if (roleRoutes[role.name]) {
          return roleRoutes[role.name];
        }
      }
    }

    // Organization-based routing
    if (considerOrganizationType && currentOrgType) {
      if (isSuperAdmin) return '/super-admin';

      if (currentOrgType === 'fleetcam') {
        if (isAdmin) return '/admin';
        if (isSupervisor) return '/bureau';
        return '/bureau';
      }

      if (currentOrgType === 'client') {
        if (isAdmin) return '/client/admin';
        if (isSupervisor) return '/client/management';
        return '/client/dashboard';
      }
    }

    // Default role-based routing
    if (isSuperAdmin) return '/super-admin';
    if (isAdmin) return '/admin';
    if (isSupervisor) return '/management';

    return fallbackRoute;
  };

  const redirect = () => {
    const path = getRedirectPath();
    if (path) {
      navigate(path, { replace: true });
    }
  };

  return { getRedirectPath, redirect };
};

export default RoleBasedRedirect;
